import { Paper } from '@mui/material'
import { useEffect, useState, useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { useExchangesListSelector } from 'redux/selectors/settings'

import Table from 'components/Table/Table'
import { balanceLevelingApi } from 'api/balance-leveling-api'
import { getAutoTopUpColumns } from './columns'
import { useSnackbar } from 'notistack'
import { RuleDialog } from './components/RuleDialog'
import { CreateRule } from './components/CreateRule'
import { Pagination, usePagination } from 'components/Pagination'
import { globalFiltersQuerySelector } from 'redux/selectors/globalFilters'
import { useSelector } from 'react-redux'

const useAllAutoTopUpRulesApi = ({ page, pageSize, setLastPage }) => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const filters = useSelector(globalFiltersQuerySelector)

  const fetch = useCallback(async (page, pageSize) => {
    setLoading(true)
    const res = await balanceLevelingApi.getAutoTopUpRules(page, pageSize, filters)

    if (res) {
      setLastPage(res.length < pageSize)
      setData(res)
    }

    setLoading(false)
  }, [setLastPage, filters])

  useEffect(() => {
    fetch(page, pageSize)
  }, [fetch, page, pageSize, filters])

  const { enqueueSnackbar } = useSnackbar()

  const toggleRule = async (ruleId, enabled) => {
    setLoading(true)
    try {
      if (enabled) {
        await balanceLevelingApi.disableAutoTopUpRule(ruleId)
        enqueueSnackbar(`Rule ${ruleId} disabled`, { variant: 'success' })
      } else {
        await balanceLevelingApi.enableAutoTopUpRule(ruleId)
        enqueueSnackbar(`Rule ${ruleId} enabled`, { variant: 'success' })
      }
    } catch (e) {
      enqueueSnackbar(e?.message || 'Unknown error', { variant: 'error' })
    }

    fetch(page, pageSize)
  }

  const deleteRule = async (ruleId) => {
    setLoading(true)
    try {
      await balanceLevelingApi.removeAutoTopUpRule(ruleId)
      enqueueSnackbar(`Rule ${ruleId} deleted`, { variant: 'success' })
      await fetch(page, pageSize)
    } catch (e) {
      enqueueSnackbar(e?.message || 'Unknown error', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const save = useCallback(async (data) => {
    setLoading(true)

    try {
      if (data.id) {
        await balanceLevelingApi.updateAutoTopUpRule(data)
        enqueueSnackbar(`Rule ${data.id} updated`, { variant: 'success' })
      } else {
        await balanceLevelingApi.createAutoTopUpRule(data)
        enqueueSnackbar(`Rule ${data.id} created`, { variant: 'success' })
      }
    } catch (e) {
      enqueueSnackbar(e?.message || 'Unknown error', { variant: 'error' })
    }

    await fetch(page, pageSize)
  }, [fetch, enqueueSnackbar, page, pageSize])

  return { data, loading, toggleRule, save, deleteRule }
}

const AllAutoTopUpRulesContainer = styled(Paper)`
  overflow-y: auto;
  margin: 4px;
`

export const AllAutoTopUpRules = () => {
  const {
    page,
    increment,
    decrement,
    pageSize,
    changePageSize,
    tableRef,
    isLastPage,
    setLastPage,
  } = usePagination({ name: 'blRulesPageSize' })
  const { data, loading, toggleRule, save, deleteRule } = useAllAutoTopUpRulesApi({ page, pageSize, setLastPage })
  const exchanges = useExchangesListSelector()
  const exchangesMap = useMemo(
    () =>
      exchanges.reduce(
        (acc, item) => ({
          ...acc,
          [item.internal_name]: item.name,
        }),
        {}
      ),
    [exchanges]
  )

  const [selectedRule, setSelectedRule] = useState(null)

  const columns = useMemo(
    () => getAutoTopUpColumns(exchangesMap, toggleRule, setSelectedRule, deleteRule),
    [exchangesMap, toggleRule, setSelectedRule, deleteRule]
  )

  return (
    <AllAutoTopUpRulesContainer elevation={2} id='balance_leveling_rules_table'>
      <Table title={<CreateRule setSelectedRule={setSelectedRule} />} data={data} columns={columns} loading={loading} enableFilters />
      <Pagination
        page={page}
        increment={increment}
        decrement={decrement}
        pageSize={pageSize}
        changePageSize={changePageSize}
        tableRef={tableRef}
        isLastPage={isLastPage}
      />
      <RuleDialog
        value={selectedRule}
        onClose={() => setSelectedRule(null)}
        save={save}
      />
    </AllAutoTopUpRulesContainer>
  )
}
